import { yousignService } from '../services/api'
import { useOnboardingStore } from '../store/onboardingStore'
import { buildReadOnlyTextFields, computeYousignDataHash } from './yousignDataHash'
import { sanitizeForYousign } from './validation'
import { logger } from './logger'
import { buildPtAnswers } from '../flow/ptAnswers'

export type EnsureSignatureResult = 'unchanged' | 'created' | 'reused'

/**
 * Create (or reuse) the Yousign signature request for the CURRENT store data.
 *
 * The SR embeds the signer's phone number at creation time — any later change
 * (e.g. the patient corrects the number on the OTP screen) MUST recreate the
 * SR, otherwise Yousign keeps sending the OTP to the old number. Both Step 6
 * (summary) and Step 9 (OTP) call this so the SR always matches the data the
 * patient sees. The server keeps its own SR ledger: same dataHash → it reuses
 * the existing signature request instead of creating a paid duplicate.
 *
 * Psychotherapy variant: the server renders every document from the answers
 * sent along (Kasse, KVNR, Probatorik choice …) — they are part of the hash, so
 * a corrected answer always gets freshly rendered documents.
 */
export async function ensureSignatureRequest(): Promise<EnsureSignatureResult> {
  const state = useOnboardingStore.getState()
  const {
    patientNumber,
    personalData,
    phoneNumber,
    addressData,
    cohort,
    insuranceData,
    yousignRequestId,
    yousignDataHash,
    setYousignData,
    setYousignDocuments,
    setYousignDataHash,
    setYousignDocumentMap,
    setPatientId,
  } = state

  const fields = buildReadOnlyTextFields({ patientNumber, personalData, phoneNumber, addressData })
  const isPt = cohort === 'psychotherapy' || cohort === 'psychotherapy_returning'
  const pt = isPt
    ? { insuranceType: insuranceData.insuranceType as 'public' | 'private' | 'none', answers: buildPtAnswers(state) }
    : undefined
  const hash = computeYousignDataHash(pt ? [...fields, { label: 'pt', text: JSON.stringify(pt) }] : fields)

  if (yousignRequestId && hash === yousignDataHash) {
    return 'unchanged'
  }

  const createResponse = await yousignService.create({
    firstName: sanitizeForYousign(personalData.firstName),
    lastName: sanitizeForYousign(personalData.lastName),
    phoneNumber,
    email: personalData.email || undefined,
    patientNumber,
    birthPlace: sanitizeForYousign(personalData.birthPlace),
    gender: personalData.gender || undefined,
    birthdate: personalData.birthDate,
    readOnlyTextFields: fields,
    dataHash: hash,
    ...(pt ? { pt } : {}),
  })

  if (!createResponse.success || !createResponse.data) {
    throw new Error('Signature request creation returned no data')
  }

  const { signatureRequestId, signerId, patientId, reused, documents, documentMap } = createResponse.data
  setYousignData(signatureRequestId, signerId, phoneNumber)
  // Psychotherapy variant: which document is which contract (the server decided the documents)
  setYousignDocumentMap(documentMap ?? null)
  if (patientId) setPatientId(patientId)

  if (reused && documents && documents.length > 0) {
    // Server reused an already-activated SR — documents come back directly
    logger.log('[Yousign] Reusing existing SR:', signatureRequestId)
    setYousignDocuments(documents)
  } else {
    logger.log('[Yousign] SR created:', { signatureRequestId, signerId })
    const activateResponse = await yousignService.activate(signatureRequestId)
    if (!activateResponse.success || !activateResponse.data?.documents) {
      throw new Error('Signature request activation failed')
    }
    setYousignDocuments(activateResponse.data.documents)
    logger.log('[Yousign] SR activated with', activateResponse.data.documents.length, 'documents')
  }

  // Persist the hash only after create AND activate succeeded — a partial
  // failure must retry from a clean state, not orphan a half-built SR
  setYousignDataHash(hash)
  return reused ? 'reused' : 'created'
}
